import React, { useEffect, useRef, useState } from "react";
import { useParams, useLocation } from "react-router-dom";
import { BOQAPI } from "../services/api";
import html2pdf from "html2pdf.js";

// ===============================
// HELPERS
// ===============================

const formatNumber = (val: any) => {
  if (val === null || val === undefined || val === "") {
    return "";
  }

  const num = Number(
    String(val).replace(/,/g, "").trim()
  );

  return Number.isFinite(num) ? num.toFixed(2) : "";
};

const getExcelQuantity = (value: any) => {
  if (value === null || value === undefined || value === "") {
    return "";
  }

  const cleaned = String(value).replace(/,/g, "").trim();
  const parsed = parseFloat(cleaned);

  if (!isNaN(parsed)) {
    return parsed;
  }

  return value;
};

const sanitizeSheetName = (name: string) => {
  return (
    (name || "BOQ")
      .replace(/[\\/*?:[\]]/g, " ")
      .substring(0, 31)
      .trim() || "BOQ"
  );
};

const cleanText = (text: any) => {
  if (text === null || text === undefined) {
    return "";
  }

  return String(text)
    .replace(/&amp;/g, "&")
    .replace(/<br\s*\/?>/gi, "\n")
    .replace(/\s+\n/g, "\n")
    .trim();
};

const approxRowHeight = (
  text: string,
  min = 18,
  max = 72
) => {
  const lines = Math.max(
    1,
    text.split("\n").length,
    Math.ceil(text.length / 65)
  );

  return Math.min(max, Math.max(min, lines * 15));
};

// ===============================
// NUMBER HELPER
// ===============================

const getNumericQuantity = (value: any): number => {
  if (
    value === null ||
    value === undefined ||
    value === ""
  ) {
    return 0;
  }

  const cleaned = String(value)
    .replace(/,/g, "")
    .trim();

  const parsed = Number(cleaned);

  return Number.isFinite(parsed) ? parsed : 0;
};

// ===============================
// NORMALIZE TEXT FOR GROUPING
// ===============================

const normalizeText = (value: any) => {
  return cleanText(value)
    .replace(/\s+/g, " ")
    .trim()
    .toLowerCase();
};

// ===============================
// SUMMARIZE WORKS
// ===============================
//
// This function:
//
// 1. Preserves activity/header rows.
// 2. Groups duplicate item descriptions.
// 3. Keeps grouping separately under each activity.
// 4. Groups by:
//
//    Activity + Description + Unit
//
// 5. Adds all duplicate quantities.
// 6. Preserves the first row's position.
// 7. Prevents materials under different activities
//    from being incorrectly combined.
//
// Example:
//
// Structural Concrete 1537.63
// Structural Concrete 1014.24
// Structural Concrete   27.09
// Structural Concrete    6.46
//
// Becomes:
//
// Structural Concrete 2585.42
// ===============================

const summarizeWorks = (works: any[] = []) => {
  const summarizedWorks: any[] = [];
  const groupedRows = new Map<string, any>();

  // Activity is supplied by the header row and belongs to all
  // following item rows until the next header.
  let currentActivity = "";

  works.forEach((work: any) => {
    // Header rows are used only to update the current activity.
    // They are not rendered as separate rows, matching the desktop PDF.
    if (work.isHeader) {
      currentActivity = cleanText(work.activity);
      return;
    }

    const activity = cleanText(work.activity || currentActivity);
    const description = cleanText(work.description);
    const unit = cleanText(work.unit);

    // Keep rows with no description separately.
    if (!description) {
      summarizedWorks.push({
        ...work,
        activity,
        description,
        unit,
        quantity: getNumericQuantity(work.quantity),
      });
      return;
    }

    // Preserve the existing 90% correct summarization rule:
    // same activity + same description + same unit = one row.
    const groupKey = [
      normalizeText(activity),
      normalizeText(description),
      normalizeText(unit),
    ].join("|||");

    const quantity = getNumericQuantity(work.quantity);
    const existingRow = groupedRows.get(groupKey);

    if (existingRow) {
      existingRow.quantity =
        getNumericQuantity(existingRow.quantity) + quantity;
    } else {
      const newRow = {
        ...work,
        activity,
        description,
        unit,
        quantity,
        isHeader: false,
      };

      groupedRows.set(groupKey, newRow);
      summarizedWorks.push(newRow);
    }
  });

  return summarizedWorks;
};

// ===============================
// WORK GROUP COMPONENT
// ===============================

const WorkGroupView = ({ group }: any) => {
  const [expanded, setExpanded] = useState(false);

  // Header rows are converted into activity names on every item row.
  // Duplicate quantities are summarized while preserving first occurrence order.
  const works = summarizeWorks(group.works || []);

  return (
    <div>
      {/* WORK GROUP HEADER */}
      <div
        onClick={() => setExpanded(!expanded)}
        style={{
          display: "flex",
          alignItems: "center",
          gap: "8px",
          fontWeight: "bold",
          background: "#e9ecef",
          padding: "6px 10px",
          borderBottom: "1px solid #ccc",
          cursor: works.length > 0 ? "pointer" : "default",
          userSelect: "none",
        }}
      >
        {works.length > 0 ? (
          <span
            className="pdf-hide-icon"
            style={{
              width: "18px",
              height: "18px",
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
              border: "1px solid #666",
              borderRadius: "3px",
              background: "#fff",
              fontSize: "14px",
              lineHeight: 1,
              flexShrink: 0,
            }}
          >
            {expanded ? "−" : "+"}
          </span>
        ) : (
          <span style={{ width: "18px" }} />
        )}

        <span>{group.workGroup}</span>
      </div>

      {/* WORK ITEMS */}
      {expanded &&
        works.map((work: any, wi: number) => (
          <div
            key={`${wi}-${work.activity || ""}-${work.description || ""}`}
            style={{
              borderBottom: "1px solid #eee",
            }}
          >
            {/* DESKTOP-STYLE ITEM ROW: ACTIVITY + DESCRIPTION */}
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "1fr 120px 80px 100px 100px",
                padding: "6px 10px",
                paddingLeft: "30px",
                fontSize: "13px",
                borderTop: "1px dashed #eee",
                background: "#fff",
              }}
            >
              {/* ACTIVITY NAME AND DESCRIPTION */}
              <div
                style={{
                  color: "#555",
                  lineHeight: "1.5",
                }}
              >
                <div
                  style={{
                    fontWeight: 400,
                    color: "#555",
                    marginBottom: "2px",
                  }}
                >
                  {work.activity}
                </div>

                <div>{work.description}</div>
              </div>

              {/* SUMMED QUANTITY */}
              <div
                style={{
                  textAlign: "right",
                  color: "#333",
                }}
              >
                {formatNumber(work.quantity)}
              </div>

              {/* UNIT */}
              <div
                style={{
                  textAlign: "center",
                  color: "#333",
                }}
              >
                {work.unit}
              </div>

              {/* UNIT COST - INTENTIONALLY BLANK */}
              <div
                style={{
                  textAlign: "right",
                  color: "#333",
                }}
              />

              {/* VALUE - INTENTIONALLY BLANK */}
              <div
                style={{
                  textAlign: "right",
                  color: "#333",
                }}
              />
            </div>
          </div>
        ))}
    </div>
  );
};

// ===============================
// TREE NODE COMPONENT
// ===============================

const TreeNodeView = ({
  node,
  level = 0,
}: any) => {
  const [expanded, setExpanded] = useState(false);

  const hasContent =
    (node.groups?.length ?? 0) > 0 ||
    (node.children?.length ?? 0) > 0;

  return (
    <div
      style={{
        marginLeft: level * 10,
      }}
    >
      {/* MAIN NODE HEADER */}
      <div
        onClick={() => {
          if (hasContent) {
            setExpanded(!expanded);
          }
        }}
        style={{
          fontWeight: "bold",
          background: "#d6d8db",
          padding: "6px 10px",
          marginTop: "10px",
          border: "1px solid #ccc",
          cursor: hasContent
            ? "pointer"
            : "default",
          display: "flex",
          alignItems: "center",
          gap: "8px",
          userSelect: "none",
        }}
      >
        {hasContent ? (
          <span
            className="pdf-hide-icon"
            style={{
              width: "18px",
              height: "18px",
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
              border: "1px solid #666",
              borderRadius: "3px",
              background: "#fff",
              fontSize: "14px",
              lineHeight: 1,
              flexShrink: 0,
            }}
          >
            {expanded ? "−" : "+"}
          </span>
        ) : (
          <span style={{ width: "18px" }} />
        )}

        <span>{node.name}</span>
      </div>

      {/* CHILD CONTENT */}
      {expanded && (
        <>
          {/* WORK GROUPS */}
          {node.groups?.map(
            (group: any, gi: number) => (
              <WorkGroupView
                key={`${group.workGroup || "group"}-${gi}`}
                group={group}
              />
            )
          )}

          {/* CHILD NODES */}
          {node.children?.map(
            (child: any, i: number) => (
              <TreeNodeView
                key={`${child.id || child.name}-${i}`}
                node={child}
                level={level + 1}
              />
            )
          )}
        </>
      )}
    </div>
  );
};

// ===============================
// MAIN PAGE
// ===============================

const BOQPage = () => {
  const { wbs } = useParams();
  const location = useLocation();

  const projectName = location.state?.projectName;

  const reportRef =
    useRef<HTMLDivElement>(null);

  const [data, setData] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);

  // ===============================
  // LOAD DATA
  // ===============================

  useEffect(() => {
    if (wbs) {
      setLoading(true);

      BOQAPI.getByWBS(Number(wbs))
        .then((res) => {
          setData(res);
        })
        // If your API returns res.data instead,
        // use:
        // .then((res) => setData(res.data))
        .catch(console.error)
        .finally(() => {
          setLoading(false);
        });
    }
  }, [wbs]);

  // ===============================
  // EXPORT PDF
  // ===============================

  const exportToPDF = async () => {
    if (!reportRef.current) {
      return;
    }

    reportRef.current.classList.add(
      "pdf-exporting"
    );

    try {
      const opt = {
        margin: 0.5,
        filename: `${
          projectName || "Project"
        }_TakeOff_Report.pdf`,
        image: {
          type: "jpeg" as const,
          quality: 1,
        },
        html2canvas: {
          scale: 2,
          useCORS: true,
        },
        jsPDF: {
          unit: "in" as const,
          format: "a4" as const,
          orientation: "portrait" as const,
        },
        pagebreak: {
          mode: [
            "avoid-all",
            "css",
            "legacy",
          ],
        },
      };

      await html2pdf()
        .set(opt)
        .from(reportRef.current)
        .save();
    } finally {
      reportRef.current.classList.remove(
        "pdf-exporting"
      );
    }
  };

  // ===============================
  // EXPORT EXCEL
  // ===============================

  const exportToExcel = async () => {
    const ExcelJS = await import("exceljs");
    const fileSaver: any = await import(
      "file-saver"
    );

    const workbook = new ExcelJS.Workbook();

    workbook.creator = "BaevrLite";
    workbook.created = new Date();

    const reportName =
      projectName ||
      data[0]?.name ||
      "Project";

    const sheetName =
      sanitizeSheetName(reportName);

    const sheet =
      workbook.addWorksheet(sheetName);

    sheet.columns = [
      {
        key: "item",
        width: 70,
      },
      {
        key: "qty",
        width: 14,
      },
      {
        key: "unit",
        width: 12,
      },
      {
        key: "unitCost",
        width: 14,
      },
      {
        key: "value",
        width: 14,
      },
    ];

    sheet.views = [
      {
        state: "frozen",
        ySplit: 2,
      },
    ];

    sheet.properties.defaultRowHeight = 18;

    // ===============================
    // TITLE
    // ===============================

    sheet.getCell("A1").value =
      `Take-Off for ${reportName}`;

    sheet.mergeCells("A1:E1");

    const titleRow = sheet.getRow(1);

    titleRow.height = 22;

    titleRow.font = {
      bold: true,
      size: 14,
    };

    titleRow.alignment = {
      vertical: "middle",
      horizontal: "left",
    };

    // ===============================
    // HEADER
    // ===============================

    const headerRow = sheet.getRow(2);

    headerRow.values = [
      "Item",
      "Quantity",
      "Unit",
      "Unit Cost",
      "Value",
    ];

    headerRow.height = 20;

    headerRow.eachCell(
      (cell, colNumber) => {
        cell.font = {
          bold: true,
          color: {
            argb: "FFFFFFFF",
          },
        };

        cell.fill = {
          type: "pattern",
          pattern: "solid",
          fgColor: {
            argb: "FF5E5E5E",
          },
        };

        cell.alignment = {
          vertical: "middle",
          horizontal:
            colNumber === 1
              ? "left"
              : "center",
        };

        cell.border = {
          top: {
            style: "thin",
            color: {
              argb: "FF8A8A8A",
            },
          },
          bottom: {
            style: "thin",
            color: {
              argb: "FF8A8A8A",
            },
          },
        };
      }
    );

    let rowIndex = 3;

    // ===============================
    // SECTION ROW STYLE
    // ===============================

    const applySectionRowStyle = (
      row: any,
      indent = 0
    ) => {
      row.height = 18;

      row.getCell(1).alignment = {
        vertical: "middle",
        horizontal: "left",
        wrapText: true,
        indent,
      };

      row.getCell(1).font = {
        bold: true,
      };

      row.getCell(1).fill = {
        type: "pattern",
        pattern: "solid",
        fgColor: {
          argb: "FFE2ECF7",
        },
      };

      for (let c = 1; c <= 5; c++) {
        const cell = row.getCell(c);

        cell.border = {
          top: {
            style: "thin",
            color: {
              argb: "FFD9D9D9",
            },
          },
          bottom: {
            style: "thin",
            color: {
              argb: "FFD9D9D9",
            },
          },
        };

        if (c > 1) {
          cell.fill = {
            type: "pattern",
            pattern: "solid",
            fgColor: {
              argb: "FFE2ECF7",
            },
          };
        }
      }

      row.getCell(2).alignment = {
        vertical: "middle",
        horizontal: "right",
      };

      row.getCell(3).alignment = {
        vertical: "middle",
        horizontal: "center",
      };

      row.getCell(4).alignment = {
        vertical: "middle",
        horizontal: "right",
      };

      row.getCell(5).alignment = {
        vertical: "middle",
        horizontal: "right",
      };
    };

    // ===============================
    // ITEM ROW STYLE
    // ===============================

    const applyItemRowStyle = (
      row: any,
      text: string
    ) => {
      row.height = approxRowHeight(text);

      for (let c = 1; c <= 5; c++) {
        const cell = row.getCell(c);

        cell.border = {
          bottom: {
            style: "thin",
            color: {
              argb: "FFD9D9D9",
            },
          },
        };

        cell.alignment = {
          vertical: "top",
          wrapText: true,
        };
      }

      row.getCell(1).alignment = {
        vertical: "top",
        horizontal: "left",
        wrapText: true,
      };

      row.getCell(2).alignment = {
        vertical: "middle",
        horizontal: "right",
      };

      row.getCell(3).alignment = {
        vertical: "middle",
        horizontal: "center",
      };

      row.getCell(4).alignment = {
        vertical: "middle",
        horizontal: "right",
      };

      row.getCell(5).alignment = {
        vertical: "middle",
        horizontal: "right",
      };
    };

    // ===============================
    // ADD NODE TO EXCEL
    // ===============================

    const addNode = (
      node: any,
      level = 0
    ) => {
      if (!node) {
        return;
      }

      // ===============================
      // MAIN NODE ROW
      // ===============================

      const mainRow =
        sheet.getRow(rowIndex++);

      mainRow.getCell(1).value =
        cleanText(node.name || "");

      mainRow.getCell(2).value = "";
      mainRow.getCell(3).value = "";
      mainRow.getCell(4).value = "";
      mainRow.getCell(5).value = "";

      applySectionRowStyle(
        mainRow,
        level
      );

      // ===============================
      // WORK GROUPS
      // ===============================

      (node.groups || []).forEach(
        (group: any) => {
          const groupRow =
            sheet.getRow(rowIndex++);

          groupRow.getCell(1).value =
            cleanText(
              group.workGroup || ""
            );

          groupRow.getCell(2).value = "";
          groupRow.getCell(3).value = "";
          groupRow.getCell(4).value = "";
          groupRow.getCell(5).value = "";

          applySectionRowStyle(
            groupRow,
            level + 1
          );

          /*
           * IMPORTANT:
           *
           * Previously Excel used:
           *
           * group.works.forEach(...)
           *
           * Now use summarized works.
           */
          const summarizedWorks =
            summarizeWorks(
              group.works || []
            );

          summarizedWorks.forEach(
            (work: any) => {
              const itemRow =
                sheet.getRow(rowIndex++);

              const itemText = [
                cleanText(
                  work.activity || ""
                ),
                cleanText(
                  work.description || ""
                ),
              ]
                .filter(Boolean)
                .join("\n");

              // ===============================
              // ACTIVITY HEADER ROW
              // ===============================

              if (work.isHeader) {
                itemRow.getCell(1).value =
                  cleanText(
                    work.activity || ""
                  );

                itemRow.getCell(2).value = "";
                itemRow.getCell(3).value = "";
                itemRow.getCell(4).value = "";
                itemRow.getCell(5).value = "";

                applySectionRowStyle(
                  itemRow,
                  level + 2
                );

                return;
              }

              // ===============================
              // SUMMARIZED ITEM ROW
              // ===============================

              itemRow.getCell(1).value =
                itemText ||
                cleanText(
                  work.description || ""
                );

              itemRow.getCell(2).value =
                getExcelQuantity(
                  work.quantity
                );

              itemRow.getCell(3).value =
                cleanText(
                  work.unit || ""
                );

              // Unit Cost and Value headings are required,
              // but their cells must remain blank.
              itemRow.getCell(4).value = "";
              itemRow.getCell(5).value = "";

              applyItemRowStyle(
                itemRow,
                itemText ||
                  cleanText(
                    work.description || ""
                  )
              );

              itemRow.getCell(2).numFmt =
                "#,##0.00";

              itemRow.getCell(4).numFmt =
                "#,##0.00";

              itemRow.getCell(5).numFmt =
                "#,##0.00";
            }
          );
        }
      );

      // ===============================
      // CHILD NODES
      // ===============================

      (node.children || []).forEach(
        (child: any) => {
          addNode(child, level + 1);
        }
      );
    };

    // ===============================
    // ADD ALL DATA
    // ===============================

    data.forEach((node) => {
      addNode(node, 0);
    });

    // ===============================
    // SAVE EXCEL
    // ===============================

    const buffer =
      await workbook.xlsx.writeBuffer();

    const blob = new Blob([buffer], {
      type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    });

    fileSaver.saveAs(
      blob,
      `${reportName}_TakeOff_Report.xlsx`
    );
  };

  // ===============================
  // LOADING
  // ===============================

  if (loading) {
    return (
      <div style={{ padding: 20 }}>
        Loading...
      </div>
    );
  }

  // ===============================
  // UI
  // ===============================

  return (
    <div
      style={{
        padding: "20px",
        fontFamily: "Arial, sans-serif",
        background: "#fff",
      }}
    >
      <style>
        {`
          .pdf-exporting .pdf-hide-icon {
            display: none !important;
          }
        `}
      </style>

      {/* PAGE HEADER */}
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginBottom: "20px",
          gap: "12px",
          flexWrap: "wrap",
        }}
      >
        <h2 style={{ margin: 0 }}>
          {projectName || "Project"} -
          Take-Off Report
        </h2>

        <div
          style={{
            display: "flex",
            gap: "10px",
            flexWrap: "wrap",
          }}
        >
          {/* EXPORT EXCEL BUTTON */}
          <button
            onClick={exportToExcel}
            style={{
              background: "#198754",
              color: "#fff",
              border: "none",
              padding: "10px 16px",
              borderRadius: "6px",
              cursor: "pointer",
              fontWeight: 600,
            }}
          >
            Export Excel
          </button>

          {/* EXPORT PDF BUTTON */}
          <button
            onClick={exportToPDF}
            style={{
              background: "#dc3545",
              color: "#fff",
              border: "none",
              padding: "10px 16px",
              borderRadius: "6px",
              cursor: "pointer",
              fontWeight: 600,
            }}
          >
            Export PDF
          </button>
        </div>
      </div>

      {/* REPORT CONTENT */}
      <div ref={reportRef}>
        {/* TABLE HEADER */}
        <div
          style={{
            display: "grid",
            gridTemplateColumns:
              "1fr 120px 80px 100px 100px",
            background: "#343a40",
            color: "white",
            padding: "8px",
            fontWeight: "bold",
          }}
        >
          <div>Item</div>

          <div
            style={{
              textAlign: "right",
            }}
          >
            Quantity
          </div>

          <div
            style={{
              textAlign: "center",
            }}
          >
            Unit
          </div>

          <div
            style={{
              textAlign: "right",
            }}
          >
            Unit Cost
          </div>

          <div
            style={{
              textAlign: "right",
            }}
          >
            Value
          </div>
        </div>

        {/* TABLE BODY */}
        <div
          style={{
            border: "1px solid #ccc",
            borderTop: "none",
          }}
        >
          {data.length === 0 ? (
            <div
              style={{
                padding: 20,
                textAlign: "center",
              }}
            >
              No data found
            </div>
          ) : (
            data.map((node, i) => (
              <TreeNodeView
                key={`${node.id || node.name}-${i}`}
                node={node}
              />
            ))
          )}
        </div>
      </div>
    </div>
  );
};

export default BOQPage;